import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { TApi, TRoutes } from '../../../../shared/const'
import { formatMinutes, mediaUrl } from '../../../../shared/courses'
import { formatVienna } from '../../../../shared/datetime'

import ModuleRow from './ModuleRow'
import PreviewBanner from './PreviewBanner'
import { certificateFileName, downloadCertificate, nextTarget, useCourse } from './data'

import styles from './style.module.scss'

/**
 * Die Startseite eines Kurses: großes Kopfbild mit „Weiter ansehen", darunter
 * eine Reihe je Modul.
 *
 * Der Stand kommt vollständig vom Server — was erledigt ist, ob ein Quiz
 * freigeschaltet ist, ob es eine Urkunde gibt. Die Ansicht entscheidet nur,
 * wohin der große Knopf führt.
 */
export default function CourseHome({ courseId }) {
	const { t } = useTranslation()
	const history = useHistory()
	const params = useParams()
	const id = courseId || params.id

	const { course, error } = useCourse(id)
	const [laedt, setLaedt] = useState(false)

	if (error) {
		return (
			<div className={styles.root}>
				<p className={styles.error}>{t('The course could not be loaded.')}</p>
			</div>
		)
	}

	if (!course) {
		return (
			<div className={styles.root}>
				<p className={styles.loading}>{t('Loading…')}</p>
			</div>
		)
	}

	const ziel = nextTarget(course)
	const progress = course.progress || {}
	const begonnen = (progress.lessonsDone || 0) > 0 || (progress.modulesPassed || 0) > 0

	const losgehen = () => {
		if (!ziel) return
		if (ziel.kind === 'quiz') history.push(TRoutes.COURSE_QUIZ(course.id, ziel.moduleId))
		else history.push(TRoutes.COURSE_LESSON(course.id, ziel.lessonId))
	}

	const urkunde = () => {
		setLaedt(true)
		downloadCertificate(TApi.COURSE_CERTIFICATE(course.id), certificateFileName(course.title)).finally(() =>
			setLaedt(false)
		)
	}

	const lessonCount = (course.modules || []).reduce((summe, modul) => summe + (modul.lessons || []).length, 0)
	const dauer = (course.modules || []).reduce((summe, modul) => summe + (modul.durationSec || 0), 0)

	return (
		<div className={styles.root}>
			{course.mode === 'PREVIEW' && <PreviewBanner />}

			<header
				className={styles.hero}
				style={course.coverUrl ? { backgroundImage: `url("${mediaUrl(course.coverUrl)}")` } : undefined}
			>
				<div className={styles.heroContent}>
					<span className={styles.eyebrow}>{t('Course')}</span>
					<h1 className={styles.heroTitle}>{course.title}</h1>
					{course.subtitle && <p className={styles.heroSubtitle}>{course.subtitle}</p>}

					<div className={styles.meta}>
						<span>{t('{{n}} modules', { n: (course.modules || []).length })}</span>
						<span>{t('{{n}} lessons', { n: lessonCount })}</span>
						{dauer > 0 && <span>{formatMinutes(dauer, t)}</span>}
						<span>{t('Certificate at the end')}</span>
					</div>

					{course.description && <p className={styles.heroText}>{course.description}</p>}

					{course.mode !== 'PREVIEW' && (
						<div className={styles.heroProgress}>
							<div className={styles.bar} aria-hidden="true">
								<span style={{ width: `${progress.percent || 0}%` }} />
							</div>
							<span>
								{course.completion
									? t('Completed on {{date}}', {
											date: formatVienna(course.completion.completedAt, 'dd.MM.yyyy')
									  })
									: t('{{percent}} % done', { percent: progress.percent || 0 })}
							</span>
						</div>
					)}

					<div className={styles.actions}>
						{ziel && (
							<button type="button" className={styles.play} onClick={losgehen}>
								<span className={styles.triangle} aria-hidden="true" />
								{ziel.kind === 'quiz'
									? t('To the quiz')
									: ziel.restart
									? t('Watch again')
									: begonnen
									? t('Continue watching')
									: t('Start')}
							</button>
						)}
						{course.completion && (
							<button type="button" className={styles.ghost} onClick={urkunde} disabled={laedt}>
								{laedt ? t('Preparing…') : t('Download certificate')}
							</button>
						)}
					</div>
				</div>
			</header>

			{(course.modules || []).map((modul, index) => (
				<ModuleRow
					key={modul.id}
					module={modul}
					index={index}
					questionsPerQuiz={course.questionsPerQuiz}
					onOpenLesson={lessonId => history.push(TRoutes.COURSE_LESSON(course.id, lessonId))}
					onOpenQuiz={moduleId => history.push(TRoutes.COURSE_QUIZ(course.id, moduleId))}
				/>
			))}
		</div>
	)
}

CourseHome.propTypes = {
	courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
